/**
 * Shared display formatters v2 (DESIGN_SYSTEM.md §7, §11.8, §14; PRD T-50). The ONE place
 * for dates, times, relative time, durations, money and numbers. Everything follows the
 * active i18n language (Hebrew → he-IL, English → en-GB); nothing outside this file
 * hard-codes a locale or calls `toLocaleString`.
 *
 * - One numeric date format in both languages: dd/MM/yyyy (he-IL Intl would give dots).
 * - Times are 24h "HH:mm".
 * - ILS through Intl (never a hard-coded "₪"), numbers with grouping.
 * - Relative time and durations come from locale keys (`format.*`), not ICU: ICU's Hebrew
 *   "לפני שעה (1)" is not how we speak (§11).
 *
 * Every function takes an optional `lang` as its last argument (tests, LTR islands); the
 * default is `i18n.language`.
 */
import i18n from '../i18n';

type DateInput = string | number | Date | null | undefined;
type NumInput = number | string | null | undefined;

/** The Intl locale for an app language ("he" → "he-IL", "en" → "en-GB"). */
export function appLocale(lang: string | null | undefined = i18n.language): 'he-IL' | 'en-GB' {
  return lang?.toLowerCase().startsWith('en') ? 'en-GB' : 'he-IL';
}

function toDate(v: DateInput): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toNum(v: NumInput): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

const tFor = (lang?: string) => (lang ? i18n.getFixedT(lang) : i18n.t.bind(i18n));

// ---------- dates and times ----------

/** 24/09/2026 — one numeric format in both languages. */
export function formatDate(v: DateInput, fallback = '—'): string {
  const d = toDate(v);
  if (!d) return fallback;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** 14:05 (24h, local time). */
export function formatTime(v: DateInput, fallback = '—'): string {
  const d = toDate(v);
  if (!d) return fallback;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 24/09/2026 14:05 */
export function formatDateTime(v: DateInput, fallback = '—'): string {
  const d = toDate(v);
  if (!d) return fallback;
  return `${formatDate(d)} ${formatTime(d)}`;
}

/**
 * An Intl date formatter in the app locale, for named parts (weekday, month name). Build it
 * once (useMemo) when formatting many dates; for a single value use `formatDateIntl`.
 */
export function dateFormatter(options: Intl.DateTimeFormatOptions, lang?: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(appLocale(lang), options);
}

/** A date with named parts in the app locale ("יום שלישי, 7 באוקטובר"). */
export function formatDateIntl(v: DateInput, options: Intl.DateTimeFormatOptions, fallback = '—', lang?: string): string {
  const d = toDate(v);
  if (!d) return fallback;
  return dateFormatter(options, lang).format(d);
}

/** "אוקטובר 2026" (long) / "אוק׳ 26" (short). */
export function formatMonthYear(v: DateInput, style: 'long' | 'short' = 'long', fallback = '—', lang?: string): string {
  return formatDateIntl(v, style === 'long' ? { month: 'long', year: 'numeric' } : { month: 'short', year: '2-digit' }, fallback, lang);
}

/**
 * A server month key ("2026-10", or a date such as "2026-10-01") → "אוקטובר 2026".
 * Read at mid-month noon, so no time zone can move it into the next or previous month.
 */
export function formatYearMonth(v: string | null | undefined, style: 'long' | 'short' = 'long', fallback = '—', lang?: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(v ?? ''));
  if (!m) return fallback;
  return formatMonthYear(new Date(Number(m[1]), Number(m[2]) - 1, 15, 12), style, fallback, lang);
}

const DAY_MS = 86_400_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Past time relative to now: "עכשיו", "לפני 5 דקות", "לפני שעתיים", "אתמול", "לפני 3 ימים";
 * a week or older, or any future time, falls back to the date ("24/09/2026").
 */
export function formatRelative(v: DateInput, now: Date | number = Date.now(), fallback = '—', lang?: string): string {
  const d = toDate(v);
  if (!d) return fallback;
  const t = tFor(lang);
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const diffMs = nowMs - d.getTime();
  // A little clock skew (server ahead of the browser) still reads "now".
  if (diffMs < -60_000) return formatDate(d);
  const minutes = Math.floor(Math.max(0, diffMs) / 60_000);
  if (minutes < 1) return t('format.relative.now');
  if (minutes < 60) return t('format.relative.minutesAgo', { count: minutes });
  const days = Math.round((startOfDay(new Date(nowMs)) - startOfDay(d)) / DAY_MS);
  const hours = Math.floor(minutes / 60);
  if (days === 0 || (days === 1 && hours < 6)) return t('format.relative.hoursAgo', { count: hours });
  if (days === 1) return t('format.relative.yesterday');
  if (days < 7) return t('format.relative.daysAgo', { count: days });
  return formatDate(d);
}

// ---------- durations ----------

/** Minutes → whole hours and minutes (negative and fractional input clamped and rounded). */
export function durationParts(totalMinutes: NumInput): { hours: number; minutes: number; padded: string } {
  const m = Math.max(0, Math.round(toNum(totalMinutes) ?? 0));
  const minutes = m % 60;
  return { hours: Math.floor(m / 60), minutes, padded: pad(minutes) };
}

/** "45 דק׳" below an hour, "3:45 שע׳" from an hour (DESIGN_SYSTEM.md §11.8). */
export function formatDuration(totalMinutes: NumInput, lang?: string): string {
  const { hours, minutes, padded } = durationParts(totalMinutes);
  const t = tFor(lang);
  return hours > 0 ? t('format.durationHours', { h: hours, mm: padded }) : t('format.durationMinutes', { m: minutes });
}

/** Minutes → "3:05" (hours:minutes, no unit): tables and totals that label the unit elsewhere. */
export function formatHoursClock(totalMinutes: NumInput): string {
  const { hours, padded } = durationParts(totalMinutes);
  return `${hours}:${padded}`;
}

// ---------- money and numbers ----------

/** An Intl number formatter in the app locale. Build once when formatting many values. */
export function numberFormatter(options: Intl.NumberFormatOptions = {}, lang?: string): Intl.NumberFormat {
  return new Intl.NumberFormat(appLocale(lang), options);
}

/** ₪1,250.00 (or another ISO currency). */
export function formatCurrency(v: NumInput, currency = 'ILS', fallback = '—', lang?: string): string {
  const n = toNum(v);
  if (n === null) return fallback;
  return numberFormatter({ style: 'currency', currency, maximumFractionDigits: 2 }, lang).format(n);
}

/** 1,250 (up to `digits` decimals, none forced). */
export function formatNumber(v: NumInput, digits = 0, fallback = '—', lang?: string): string {
  const n = toNum(v);
  if (n === null) return fallback;
  return numberFormatter({ maximumFractionDigits: digits, minimumFractionDigits: 0 }, lang).format(n);
}

/** 12.5K / 1.2M (chart axes and tight tiles). */
export function formatCompact(v: NumInput, fallback = '—', lang?: string): string {
  const n = toNum(v);
  if (n === null) return fallback;
  return numberFormatter({ notation: 'compact', maximumFractionDigits: 1 }, lang).format(n);
}
